import { useState, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch, FormControlLabel, Tooltip } from '@mui/material';
import ResultsStore from '../../../stores/ResultsStore';
import PointsStore from '../../../stores/PointsStore';
import ProfileStore from '../../../stores/ProfileStore';
import SearchStore from '../../../stores/SearchStore';
import SearchService from '../../../utils/SearchService';
import { isElalCompany } from '@/utils/isCompany';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUseMaxPointsFromLocation } from 'arbitrip-common/general/utils/UrlUtils';
import Analytics from 'arbitrip-common/client/analytics';
import { getPointsToggleState } from 'arbitrip-common/client/analytics/services/loyaltyService';

const PointsToggleComponent = memo(({ withTooltip, tooltipClass = '' }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const isElal = isElalCompany();

    const [showPricesInPoints, setShowPricesInPoints] = useState(() => ResultsStore.getShowPricesInPoints());
    const [pointsBalance, setPointsBalance] = useState(() => PointsStore.getBalance());

    const profile = ProfileStore.getProfile();
    const isAnon = Boolean(profile && profile.anonymous && !profile.isLoggedIn);

    useEffect(() => {
        const handleStoreChange = () => {
            const newShowPrices = ResultsStore.getShowPricesInPoints();
            const newPoints = PointsStore.getBalance();
            setShowPricesInPoints((prev) => (prev !== newShowPrices ? newShowPrices : prev));
            setPointsBalance((prev) => (prev !== newPoints ? newPoints : prev));
        };
        ResultsStore.addChangeListener(handleStoreChange);
        PointsStore.addChangeListener(handleStoreChange);
        return () => {
            ResultsStore.removeChangeListener(handleStoreChange);
            PointsStore.removeChangeListener(handleStoreChange);
        };
    }, []);

    const handleToggle = (event) => {
        const previousState = getPointsToggleState(ResultsStore.getShowPricesInPoints());
        const isCheckedRaw = event.target.checked;
        const isChecked = isCheckedRaw && (isAnon || pointsBalance > 0);
        ResultsStore.setShowPricesInPoints(isChecked);
        const newState = getPointsToggleState(isChecked);
        if (newState !== previousState) {
            const searchTerms = SearchStore.getSearchTerms();
            const nights = SearchService.getNumberOfNights(searchTerms);
            Analytics.actions.points.toggledPointsDisplay({
                user: ProfileStore.getProfile(),
                toggle_new_state: newState,
                toggle_previous_state: previousState,
                points_balance: PointsStore.getBalance(),
                search_destination: searchTerms?.destination?.address,
                ...(Number.isFinite(nights) && !isNaN(nights) ? { search_nights: nights } : {}),
            });
        }
    };

    if (!isElal) {
        return null;
    }

    const isDisabled = !isAnon && pointsBalance === 0;

    return (
        <Tooltip
            title={t('NoPointsTooltip')}
            placement={'bottom'}
            arrow
            classes={{ tooltip: tooltipClass || 'points-tooltip' }}
            disableHoverListener={!(withTooltip && isDisabled)}
        >
            <div className={`points-toggle-component ${isDisabled ? 'disabled' : ''}`}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showPricesInPoints}
                            onChange={handleToggle}
                            color="primary"
                            data-testid="points-toggle-switch"
                            disabled={isDisabled}
                        />
                    }
                    label={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span className="points-toggler-extra-text">
                                {t('ShowPricesInPoints')}
                            </span>
                        </div>
                    }
                    labelPlacement="start"
                />
            </div>
        </Tooltip>
    );
});

PointsToggleComponent.displayName = 'PointsToggleComponent';

export default PointsToggleComponent;
